//client/src/pages/ProfilePage.js - Updated for New Plan Structure
import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { authedFetch } from '../utils/authedFetch';
import { apiUrl } from '../utils/apiBase';
import { validatePassword } from '../utils/passwordPolicy';

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [commPref, setCommPref] = useState('both');
  
  // Game preferences - new structure
  const [pbEnabled, setPbEnabled] = useState(true);
  const [pbDrawDays, setPbDrawDays] = useState({ monday: true, wednesday: true, saturday: true });
  const [mmEnabled, setMmEnabled] = useState(true);
  const [mmDrawDays, setMmDrawDays] = useState({ tuesday: true, friday: true });
  const [jackpotThreshold, setJackpotThreshold] = useState(500);
  
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [resending, setResending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState(null); // { message, color }
  const toastTimerRef = useRef(null);
  const navigate = useNavigate();

  // Account status helpers
  const isAccountRestricted = (status) => status !== "active";
  const isAccountPaused = (status) => status === "paused";
  const isAccountSuspended = (status) => status === "suspended";
  const isAccountPaymentFailed = (status) => status === "payment_failed";
  const accountStatus = profile?.accountInfo?.accountStatus || "active";

  // Plan helpers
  const plan = profile?.subscription?.plan || "jackpotChaser";
  const isWeeklyPlayer = plan === "weeklyPlayer";
  const isJackpotChaser = plan === "jackpotChaser";

  // Threshold options based on plan
  const jackpotChaserThresholds = [
    { value: 500, label: '$500 Million+' },
    { value: 750, label: '$750 Million+' },
    { value: 1000, label: '$1 Billion+' }
  ];

  const weeklyPlayerThresholds = [
    { value: 0, label: 'All Drawings (no threshold)' },
    { value: 100, label: '$100 Million+' },
    { value: 200, label: '$200 Million+' },
    { value: 300, label: '$300 Million+' },
    { value: 400, label: '$400 Million+' },
    { value: 500, label: '$500 Million+' }
  ];

  const currentThresholds = isWeeklyPlayer ? weeklyPlayerThresholds : jackpotChaserThresholds;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (!user) return navigate('/login');

      try {
        const snap = await getDoc(doc(db, 'members', user.email));
        setLoadError(false);
        if (snap.exists()) {
          const d = snap.data();
          setProfile(d);

          // Check if account is deleted - force logout
          if (d.accountInfo?.accountStatus === "deleted") {
            console.log("🗑️ Deleted account detected - forcing logout");
            await signOut(auth);
            alert("This account has been deleted and is no longer accessible.");
            navigate('/login');
            return;
          }

          setAutoRenew(d.subscription?.autoRenew ?? d.autoRenew ?? true);
          setCommPref(d.communicationPreferences?.method ?? 'both');

          // Load game preferences from new structure
          if (d.gamePreferences) {
            setJackpotThreshold(d.gamePreferences.jackpotThreshold ?? 500);
            setPbEnabled(d.gamePreferences.powerball?.enabled ?? true);
            setPbDrawDays(d.gamePreferences.powerball?.drawDays ?? { monday: true, wednesday: true, saturday: true });
            setMmEnabled(d.gamePreferences.megamillions?.enabled ?? true);
            setMmDrawDays(d.gamePreferences.megamillions?.drawDays ?? { tuesday: true, friday: true });
          } else {
            // Backwards compatibility: load from old games array
            const games = Array.isArray(d.games) ? d.games : ['powerball', 'megamillions'];
            setPbEnabled(games.includes('powerball'));
            setMmEnabled(games.includes('megamillions'));
          }
        }
      } catch (err) {
        // Permission/network failure must not leave the page stuck on "Loading…"
        console.error('❌ Failed to load profile:', err);
        setLoadError(true);
      }
    });
    return () => unsub();
  }, [navigate, reloadKey]);

  const savePrefs = async () => {
    if (isAccountRestricted(accountStatus)) {
      alert("Account functionality is restricted. Please resolve account status first.");
      return;
    }

    // Build the games array for backwards compatibility
    const gamesArray = [];
    if (pbEnabled) gamesArray.push('powerball');
    if (mmEnabled) gamesArray.push('megamillions');

// Sync auto-renew with Stripe FIRST
    const previousAutoRenew = profile.subscription?.autoRenew ?? true;
    if (autoRenew !== previousAutoRenew) {
try {
        await callSubscriptionManager(autoRenew ? 'setAutoRenewOn' : 'setAutoRenewOff');
        setProfile(prev => ({
          ...prev,
          subscription: { ...prev.subscription, autoRenew: autoRenew }
        }));
      } catch (error) {
        alert('Failed to update auto-renewal in Stripe: ' + error.message);
        return; // Don't save to Firestore if Stripe update failed
      }
    }

    await updateDoc(doc(db, 'members', profile.accountInfo.email), { 
      communicationPreferences: {
        method: commPref,
        notificationsEnabled: profile.communicationPreferences?.notificationsEnabled ?? true,
        createdAt: profile.communicationPreferences?.createdAt || new Date().toISOString(),
      },
      games: gamesArray,
      gamePreferences: {
        jackpotThreshold: jackpotThreshold,
        powerball: {
          enabled: pbEnabled,
          drawDays: pbDrawDays
        },
        megamillions: {
          enabled: mmEnabled,
          drawDays: mmDrawDays
        }
      }
    });

    // Show success message
    showToast('✓ Changes saved successfully!', '#22c55e');
  };

  // State-driven toast (rendered in JSX below) — no manual DOM nodes
  const showToast = (message, color = '#22c55e') => {
    clearTimeout(toastTimerRef.current);
    setToast({ message, color });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  };

  // Clear any pending toast timer on unmount
  useEffect(() => {
    return () => clearTimeout(toastTimerRef.current);
  }, []);

const handleReactivateAccount = async () => {
    try {
      const result = await callSubscriptionManager('resume');
      setProfile(prev => ({
        ...prev,
        accountInfo: { ...prev.accountInfo, accountStatus: "active" },
        subscription: { 
          ...prev.subscription, 
          status: "active", 
          autoRenew: true,
          renewalDate: result.renewalDate 
        }
      }));
      showToast('✅ Account reactivated — billing resumed!', '#22c55e');
    } catch (error) {
      console.error("❌ Reactivation failed:", error);
      alert("Failed to reactivate account: " + error.message);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (isAccountRestricted(accountStatus) && !isAccountSuspended(accountStatus)) {
      alert("Cannot change password while account is restricted.");
      return;
    }
    
    setPasswordError('');
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Same policy as signup (utils/passwordPolicy.js)
    const policyErrors = validatePassword(passwordForm.newPassword);
    if (policyErrors.length > 0) {
      setPasswordError(`Password must have: ${policyErrors.join(', ')}`);
      return;
    }

    try {
      const { updatePassword, reauthenticateWithCredential, EmailAuthProvider } = await import('firebase/auth');
      const credential = EmailAuthProvider.credential(auth.currentUser.email, passwordForm.currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, passwordForm.newPassword);
      
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showToast('✓ Password updated successfully!', '#22c55e');
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('Password is too weak');
      } else {
        setPasswordError('Failed to update password. Please try again.');
      }
    }
  };

const callSubscriptionManager = async (action) => {
    try {
      const response = await authedFetch(apiUrl('manageSubscription'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.accountInfo.email, action })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error(`❌ Subscription action "${action}" failed:`, error);
      throw error;
    }
  };

  const handleResendNumbers = async () => {
    setResending(true);
    try {
      const response = await authedFetch(apiUrl('resendLatestNumbers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.accountInfo.email })
      });

      if (!response.ok) throw new Error('Failed to resend numbers');
      showToast('📨 Latest numbers sent successfully!', '#8b5cf6');
    } catch (error) {
      console.error('Failed to resend numbers:', error);
      alert('Failed to resend numbers. Please try again or contact support.');
    } finally {
      setResending(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  if (loadError && !profile) {
    return (
      <div style={{
        textAlign: 'center',
        margin: '2rem auto',
        maxWidth: '400px',
        padding: '1.5rem',
        fontFamily: "'Inter', 'Open Sans', sans-serif"
      }}>
        <div style={alertStyle('#fef2f2', '#ef4444', '#dc2626')}>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>⚠️ Couldn't load your profile</h3>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Something went wrong loading your account. Please check your connection and try again.
          </p>
        </div>
        <button
          onClick={() => { setLoadError(false); setReloadKey(k => k + 1); }}
          style={primaryButton}
        >
          🔄 Try Again
        </button>
      </div>
    );
  }

  if (!profile) return <p style={{ textAlign: 'center', margin: '2rem' }}>Loading…</p>;

  const { firstName } = profile.accountInfo || {};
  const signupDate = profile.subscription?.signupDate || profile.signupDate;
  const renewalDate = profile.subscription?.renewalDate || profile.subscription?.nextBillingDate;
  const isRestricted = isAccountRestricted(accountStatus);
  const isPaused = isAccountPaused(accountStatus);
  const isSuspended = isAccountSuspended(accountStatus);
  const isPaymentFailed = isAccountPaymentFailed(accountStatus);

  return (
    <>
      {/* Toast notification (state-driven) */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          color: '#2d3748',
          padding: '1rem 2rem',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          borderLeft: `4px solid ${toast.color}`,
          zIndex: 9999,
          fontFamily: 'inherit',
          fontWeight: 600
        }}>
          {toast.message}
        </div>
      )}

      <div style={{
        padding: '1rem',
        maxWidth: '500px',
        margin: '0 auto',
        fontFamily: "'Inter', 'Open Sans', sans-serif",
        backgroundColor: '#f8fafc',
        minHeight: '100vh'
      }}>
        {/* Admin Dashboard Button */}
        {profile.accountInfo?.email === 'admin@luckylogic.ai' && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={() => navigate('/admin-dashboard')}
              style={{ ...primaryButton, backgroundColor: '#1E9FF2' }}
            >
              🔧 Admin Dashboard
            </button>
          </div>
        )}

        {/* Account Status Alerts */}
        {isPaused && (
          <div style={alertStyle('#fef3c7', '#f59e0b', '#92400e')}>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>⏸️ Account Paused</h3>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Your account is paused. Click "Reactivate Account" below to restore full access.
            </p>
          </div>
        )}

        {isSuspended && (
          <div style={alertStyle('#fee2e2', '#dc2626', '#991b1b')}>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>🚫 Account Suspended</h3>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Your subscription has expired. Turn on auto-renewal to reactivate your account.
            </p>
          </div>
        )}

        {isPaymentFailed && (
          <div style={alertStyle('#fef2f2', '#ef4444', '#dc2626')}>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>💳 Payment Failed</h3>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Your last payment failed. Please update your payment method or contact support.
            </p>
          </div>
        )}

        {/* Welcome Header */}
        <div style={{
          background: 'linear-gradient(135deg, #A6CE3A 0%, #8BB62A 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1rem',
          color: 'white',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(166, 206, 58, 0.3)',
          opacity: isPaused ? 0.6 : 1
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
            Welcome back, {firstName}!
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
            {isPaused ? 'Account is paused' : 'Managing your LuckyLogic.ai account'}
          </p>
        </div>

        {/* Account Info Card */}
        <div style={cardStyle}>
          <h3 style={cardHeaderStyle}>🔐 Account Info</h3>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={infoRow}>
              <span style={labelStyle}>Email</span>
              <span style={valueStyle}>{profile.accountInfo?.email}</span>
            </div>
            <div style={infoRow}>
              <span style={labelStyle}>Phone</span>
              <span style={valueStyle}>{profile.accountInfo?.phone}</span>
            </div>
            <div style={infoRow}>
              <span style={labelStyle}>Location</span>
              <span style={valueStyle}>{profile.accountInfo?.city}, {profile.accountInfo?.state} {profile.accountInfo?.zip}</span>
            </div>
          </div>
          <button
            onClick={() => setShowPasswordModal(true)}
            disabled={isPaused || isPaymentFailed}
            style={{
              ...secondaryButton,
              opacity: (isPaused || isPaymentFailed) ? 0.5 : 1,
              cursor: (isPaused || isPaymentFailed) ? 'not-allowed' : 'pointer'
            }}
          >
            🔑 Change Password
          </button>
        </div>

        {/* Subscription Card */}
        <div style={cardStyle}>
          <h3 style={cardHeaderStyle}>💳 Subscription</h3>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={infoRow}>
              <span style={labelStyle}>Plan</span>
              <span style={{ ...valueStyle, color: '#A6CE3A', fontWeight: '700' }}>
                {isWeeklyPlayer ? 'Weekly Player' : 'Jackpot Chaser'}
              </span>
            </div>
            <div style={infoRow}>
              <span style={labelStyle}>Price</span>
              <span style={valueStyle}>{isWeeklyPlayer ? '$34.99/year' : '$14.99/year'}</span>
            </div>
            <div style={infoRow}>
              <span style={labelStyle}>Member Since</span>
              <span style={valueStyle}>{signupDate ? new Date(signupDate).toLocaleDateString() : 'Recent'}</span>
            </div>
            <div style={infoRow}>
              <span style={labelStyle}>Renewal Date</span>
              <span style={valueStyle}>{renewalDate ? new Date(renewalDate).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>
          
          {/* Auto-renewal toggle */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.75rem',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            cursor: (isPaused || isPaymentFailed) ? 'not-allowed' : 'pointer',
            opacity: (isPaused || isPaymentFailed) ? 0.5 : 1
          }}>
            <input 
              type="checkbox" 
              checked={autoRenew} 
              onChange={() => setAutoRenew(!autoRenew)}
              disabled={isPaused || isPaymentFailed}
              style={{ marginRight: '0.75rem', transform: 'scale(1.2)' }}
            />
            <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>Auto-renew subscription</span>
          </label>
        </div>

        {/* Communication Preferences Card */}
        <div style={cardStyle}>
          <h3 style={cardHeaderStyle}>💬 Communication Preferences</h3>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
            How would you like to receive your numbers?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {['text', 'email', 'both'].map((option) => (
              <label key={option} style={{
                ...radioOption,
                backgroundColor: commPref === option ? '#f0fdf4' : '#f8fafc',
                borderColor: commPref === option ? '#A6CE3A' : '#e5e7eb',
                opacity: isRestricted ? 0.5 : 1,
                cursor: isRestricted ? 'not-allowed' : 'pointer'
              }}>
                <input 
                  type="radio" 
                  checked={commPref === option} 
                  onChange={() => setCommPref(option)}
                  disabled={isRestricted}
                  style={{ marginRight: '0.75rem' }}
                />
                <span>
                  {option === 'text' ? '📱 Text only' : 
                   option === 'email' ? '📧 Email only' : 
                   '📱📧 Both'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Game Preferences Card */}
        <div style={cardStyle}>
          <h3 style={cardHeaderStyle}>🎯 Game Preferences</h3>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
            Select which games you want to receive numbers for.
          </p>

          {/* Powerball Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              ...radioOption,
              backgroundColor: pbEnabled ? '#f0fdf4' : '#f8fafc',
              borderColor: pbEnabled ? '#A6CE3A' : '#e5e7eb',
              marginBottom: '0.5rem'
            }}>
              <input 
                type="checkbox" 
                checked={pbEnabled} 
                onChange={() => setPbEnabled(!pbEnabled)}
                disabled={isRestricted}
                style={{ marginRight: '0.75rem', transform: 'scale(1.2)' }}
              />
              <div>
                <span style={{ fontWeight: '600' }}>🎲 Powerball</span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                  (Mon, Wed, Sat)
                </span>
              </div>
            </label>
            
            {/* Powerball Draw Days - Weekly Player only */}
            {isWeeklyPlayer && pbEnabled && (
              <div style={{ marginLeft: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'monday', label: 'Mon' },
                  { key: 'wednesday', label: 'Wed' },
                  { key: 'saturday', label: 'Sat' }
                ].map(({ key, label }) => (
                  <label key={key} style={{
                    ...dayToggle,
                    backgroundColor: pbDrawDays[key] ? '#A6CE3A' : '#f8fafc',
                    color: pbDrawDays[key] ? 'white' : '#374151',
                    borderColor: pbDrawDays[key] ? '#A6CE3A' : '#e5e7eb'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={pbDrawDays[key]} 
                      onChange={() => setPbDrawDays(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{ display: 'none' }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Mega Millions Section */}
          <div>
            <label style={{
              ...radioOption,
              backgroundColor: mmEnabled ? '#f0fdf4' : '#f8fafc',
              borderColor: mmEnabled ? '#A6CE3A' : '#e5e7eb',
              marginBottom: '0.5rem'
            }}>
              <input 
                type="checkbox" 
                checked={mmEnabled} 
                onChange={() => setMmEnabled(!mmEnabled)}
                disabled={isRestricted}
                style={{ marginRight: '0.75rem', transform: 'scale(1.2)' }}
              />
              <div>
                <span style={{ fontWeight: '600' }}>💎 Mega Millions</span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                  (Tue, Fri)
                </span>
              </div>
            </label>
            
            {/* Mega Millions Draw Days - Weekly Player only */}
            {isWeeklyPlayer && mmEnabled && (
              <div style={{ marginLeft: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'tuesday', label: 'Tue' },
                  { key: 'friday', label: 'Fri' }
                ].map(({ key, label }) => (
                  <label key={key} style={{
                    ...dayToggle,
                    backgroundColor: mmDrawDays[key] ? '#A6CE3A' : '#f8fafc',
                    color: mmDrawDays[key] ? 'white' : '#374151',
                    borderColor: mmDrawDays[key] ? '#A6CE3A' : '#e5e7eb'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={mmDrawDays[key]} 
                      onChange={() => setMmDrawDays(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{ display: 'none' }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Jackpot Threshold Card */}
        <div style={cardStyle}>
          <h3 style={cardHeaderStyle}>💰 Jackpot Threshold</h3>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
            {isJackpotChaser 
              ? 'You will only receive numbers when the jackpot reaches this amount.'
              : 'Choose when you want to receive numbers, or get them for every drawing.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {currentThresholds.map(({ value, label }) => (
              <label key={value} style={{
                ...radioOption,
                backgroundColor: jackpotThreshold === value ? '#f0fdf4' : '#f8fafc',
                borderColor: jackpotThreshold === value ? '#A6CE3A' : '#e5e7eb',
                opacity: isRestricted ? 0.5 : 1,
                cursor: isRestricted ? 'not-allowed' : 'pointer'
              }}>
                <input 
                  type="radio" 
                  checked={jackpotThreshold === value} 
                  onChange={() => setJackpotThreshold(value)}
                  disabled={isRestricted}
                  style={{ marginRight: '0.75rem' }}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Past Numbers Card */}
        <div style={cardStyle}>
          <h3 style={cardHeaderStyle}>🎟️ My Past Numbers</h3>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
            View all the numbers you've been assigned for past draws.
          </p>
          <button
            onClick={() => navigate('/past-numbers')}
            style={secondaryButton}
          >
            🎟️ View My Past Numbers
          </button>
        </div>

        {/* Resend Numbers Card - only for active accounts */}
        {accountStatus === "active" && (
          <div style={cardStyle}>
            <h3 style={cardHeaderStyle}>📨 Resend Latest Numbers</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
              Didn't receive your latest lottery numbers? Click below to resend them.
            </p>
            <button
              onClick={handleResendNumbers}
              disabled={resending}
              style={{
                ...secondaryButton,
                opacity: resending ? 0.5 : 1,
                cursor: resending ? 'not-allowed' : 'pointer'
              }}
            >
              {resending ? '📤 Sending...' : '📨 Resend My Latest Numbers'}
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem', marginBottom: '2rem' }}>
          <button 
            onClick={savePrefs}
            disabled={isRestricted}
            style={{
              ...primaryButton,
              opacity: isRestricted ? 0.5 : 1,
              cursor: isRestricted ? 'not-allowed' : 'pointer'
            }}
          >
            💾 Save Changes
          </button>
          
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              onClick={() => {
                if (isPaused) {
                  handleReactivateAccount();
                } else if (!isRestricted) {
                  setShowAccountModal(true);
                }
              }}
              disabled={isRestricted && !isPaused}
              style={{
                ...secondaryButton,
                flex: 1,
                backgroundColor: isPaused ? '#A6CE3A' : isRestricted ? '#9ca3af' : '#DC3545',
                color: 'white',
                opacity: (isRestricted && !isPaused) ? 0.5 : 1,
                cursor: (isRestricted && !isPaused) ? 'not-allowed' : 'pointer'
              }}
            >
              {isPaused ? '✅ Reactivate' : isRestricted ? 'Restricted' : 'Close Account'}
            </button>
            
            <button 
              onClick={logout} 
              style={{ ...secondaryButton, flex: 1, backgroundColor: '#6b7280', color: 'white' }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{ margin: '0 0 1.5rem 0', textAlign: 'center' }}>🔑 Change Password</h3>
            
            {passwordError && (
              <div style={{ padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '8px', 
                           borderLeft: '4px solid #ef4444', color: '#dc2626', marginBottom: '1rem' }}>
                {passwordError}
              </div>
            )}

            <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="password"
                placeholder="Current Password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                required
                style={inputStyle}
              />
              <input
                type="password"
                placeholder="New Password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                required
                style={inputStyle}
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                required
                style={inputStyle}
              />
              
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError('');
                  }} 
                  style={{ ...secondaryButton, flex: 1, backgroundColor: '#6b7280', color: 'white' }}
                >
                  Cancel
                </button>
                <button type="submit" style={{ ...primaryButton, flex: 1 }}>
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Actions Modal */}
      {showAccountModal && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{ margin: '0 0 1.5rem 0', textAlign: 'center' }}>⚙️ Account Actions</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
<button 
                onClick={async () => {
                  try {
                    await callSubscriptionManager('pause');
                    setProfile(prev => ({
                      ...prev,
                      accountInfo: { ...prev.accountInfo, accountStatus: "paused" }
                    }));
                    setShowAccountModal(false);
                    showToast('⏸️ Account paused — billing suspended', '#f59e0b');
                  } catch (error) {
                    alert('Failed to pause account: ' + error.message);
                  }
                }}
                style={{ ...secondaryButton, backgroundColor: '#f59e0b', color: 'white', textAlign: 'left', padding: '1rem' }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>⏸️ Pause My Account</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                  Temporarily close your account - you can reactivate anytime
                </div>
              </button>

              <button 
                onClick={() => {
                  setShowAccountModal(false);
                  setShowDeleteConfirm(true);
                }}
                style={{ ...secondaryButton, backgroundColor: '#dc2626', color: 'white', textAlign: 'left', padding: '1rem' }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>🗑️ Delete My Account</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                  Permanently close your account - this cannot be undone
                </div>
              </button>

              <button 
                onClick={() => setShowAccountModal(false)}
                style={{ ...secondaryButton, backgroundColor: '#6b7280', color: 'white' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#dc2626', textAlign: 'center' }}>
              🗑️ Delete Account Permanently
            </h3>
            
            <div style={{ margin: '0 0 1.5rem 0', color: '#374151', lineHeight: '1.5' }}>
              <p><strong>This will permanently close your account.</strong></p>
              <p>• You will never receive numbers or emails again</p>
              <p>• This action cannot be undone</p>
              <p>• We'll retain records for legal compliance as permitted by law</p>
              <p>• Records are auto-deleted after 7 years</p>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                style={{ ...secondaryButton, flex: 1, backgroundColor: '#6b7280', color: 'white' }}
              >
                Cancel
              </button>
 <button 
                onClick={async () => {
                  try {
                    await callSubscriptionManager('delete');
                    setShowDeleteConfirm(false);
                    setTimeout(() => {
                      alert("Account deleted. Your subscription has been cancelled and no further charges will occur.");
                      signOut(auth);
                      navigate('/');
                    }, 500);
                  } catch (error) {
                    alert('Failed to delete account: ' + error.message);
                    setShowDeleteConfirm(false);
                  }
                }}
                style={{ ...secondaryButton, flex: 1, backgroundColor: '#dc2626', color: 'white' }}
              >
                Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Simplified Styles
const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '1.25rem',
  marginBottom: '1rem',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  border: '1px solid #e5e7eb'
};

const cardHeaderStyle = {
  margin: '0 0 1rem 0',
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#1f2937'
};

const infoRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const labelStyle = {
  fontSize: '0.85rem',
  color: '#6b7280',
  fontWeight: '500'
};

const valueStyle = {
  fontSize: '0.95rem',
  fontWeight: '600',
  color: '#1f2937',
  textAlign: 'right'
};

const radioOption = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.75rem',
  borderRadius: '8px',
  border: '2px solid #e5e7eb',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontSize: '0.95rem'
};

const dayToggle = {
  padding: '0.5rem 1rem',
  borderRadius: '20px',
  border: '2px solid #e5e7eb',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '0.85rem',
  transition: 'all 0.2s ease'
};

const primaryButton = {
  padding: '0.875rem 1.5rem',
  backgroundColor: '#A6CE3A',
  color: 'white',
  fontWeight: '600',
  fontSize: '1rem',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  width: '100%',
  minHeight: '48px'
};

const secondaryButton = {
  padding: '0.75rem 1rem',
  backgroundColor: '#f8fafc',
  color: '#374151',
  fontWeight: '600',
  fontSize: '0.95rem',
  border: '2px solid #e5e7eb',
  borderRadius: '10px',
  cursor: 'pointer',
  width: '100%',
  minHeight: '44px'
};

const alertStyle = (bg, border, text) => ({
  backgroundColor: bg,
  borderRadius: '12px',
  padding: '1rem',
  marginBottom: '1rem',
  border: `2px solid ${border}`,
  textAlign: 'center',
  color: text
});

const modalOverlay = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem'
};

const modalContent = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '1.5rem',
  maxWidth: '400px',
  width: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
};

const inputStyle = {
  padding: '0.875rem',
  fontSize: '1rem',
  borderRadius: '8px',
  border: '2px solid #e5e7eb',
  outline: 'none',
  fontFamily: 'inherit'
};

export default ProfilePage;